import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["enabled", "label", "testingIdentifier", "reduxEntity", "path", "content", "value", "userID", "maxFileSize", "highResolutionImages", "notificationActions", "onChange", "mdRows"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useCallback } from 'react';
import { parentPath, getValue, pathComparator } from '@truqc/content-path';
import PropTypes from 'prop-types';
import { v4 as uuidV4 } from 'uuid';
import * as ramda from 'ramda';
import { computeHeightInPixels } from '../heightProps';
import { enabledSelector, labelSelector, newPathSelector, valueSelector } from '../selectors/index';
import entityComposition from '../../common/classes/entityComposition';
import Upload from '../../common/controls/upload';
import Lightbox from '../../common/lightbox';
import Label from './label';
import { getContent } from '../selectors/simpleSelectors';
import uploadUtils from '../../../utils/uploadUtils';
import Snapper from '../../common/snapper';
import checkBrowser from '../../../utils/checkBrowser';
import logValueOnChange from '../commonActions/logValueOnChange';
const PhotoPickerField = _ref => {
  let enabled = _ref.enabled,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? null : _ref$label,
    _ref$testingIdentifie = _ref.testingIdentifier,
    testingIdentifier = _ref$testingIdentifie === void 0 ? null : _ref$testingIdentifie,
    _ref$reduxEntity = _ref.reduxEntity,
    reduxEntity = _ref$reduxEntity === void 0 ? 'report' : _ref$reduxEntity,
    path = _ref.path,
    content = _ref.content,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value,
    userID = _ref.userID,
    _ref$maxFileSize = _ref.maxFileSize,
    maxFileSize = _ref$maxFileSize === void 0 ? {} : _ref$maxFileSize,
    _ref$highResolutionIm = _ref.highResolutionImages,
    highResolutionImages = _ref$highResolutionIm === void 0 ? false : _ref$highResolutionIm,
    notificationActions = _ref.notificationActions,
    onChange = _ref.onChange,
    _ref$mdRows = _ref.mdRows,
    mdRows = _ref$mdRows === void 0 ? 8 : _ref$mdRows,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  const newProps = _objectSpread(_objectSpread({}, otherProps), {}, {
    label,
    testingIdentifier,
    reduxEntity,
    value,
    maxFileSize,
    highResolutionImages,
    mdRows
  });
  const _useState = useState(() => value && (value._cache || value._downloadURL) ? value._cache || value._downloadURL : ''),
    _useState2 = _slicedToArray(_useState, 2),
    imageSrc = _useState2[0],
    setImageSrc = _useState2[1];

  // Update imageSrc when value prop changes
  useEffect(() => {
    const newImageSrc = value && (value._cache || value._downloadURL) ? value._cache || value._downloadURL : '';
    setImageSrc(newImageSrc);
  }, [value]);

  // Cleanup on unmount
  useEffect(() => () => {
    // clear the reduxEntity upload state so there aren't any lingering values
    /* eslint-disable-next-line react/destructuring-assignment */
    newProps["".concat(reduxEntity, "Actions")].uploadClear();
    // if reduxEntity changes, the uploadClear method also changes, no need to watch both.
  }, [reduxEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = useCallback(async filesToUpload => {
    if (!filesToUpload.length) return;
    try {
      const file = filesToUpload[0]; // PhotoPickerField only handles one file

      const baseFileName = uuidV4().toUpperCase();

      // Upload image with compression (if applicable) handled internally
      // Note: uploadUtils.uploadImage handles file size validation internally as well
      const uploadResult = await uploadUtils.uploadImage({
        file,
        baseFileName,
        highResEnabled: highResolutionImages,
        maxFileSize
      });

      // Check for errors
      if (uploadResult.errors.length > 0) {
        const hasBlockingErrors = uploadResult.errors.some(error => error.includes('Upload failed') || error.includes('exceeds the maximum allowed size') && !error.includes('compressed version uploaded'));
        if (hasBlockingErrors) {
          // Blocking error - show error and don't proceed
          notificationActions.error({
            message: uploadResult.errors.join(' '),
            id: 'upload_failed'
          }, {
            autoClose: 10000
          });
          return;
        }
        // Non-blocking errors (like low-res upload failure or compressed upload)
        // show warning but continue
        notificationActions.error({
          message: uploadResult.errors.join(' '),
          id: 'low_upload_warning'
        }, {
          autoClose: 8000
        });
      }

      // Update content using the onChange mechanism with simplified remotePath
      await onChange({
        path,
        value: uploadResult.remotePath
      });
    } catch (error) {
      notificationActions.error({
        message: "Failed to process image: ".concat(error.message),
        id: 'image_processing_failed'
      }, {
        autoClose: 10000
      });
    }
    // if reduxEntity changes, the serverUpload method also changes, no need to watch both.
  }, [
  // eslint-disable-line react-hooks/exhaustive-deps
  path, userID, reduxEntity, maxFileSize, highResolutionImages, notificationActions]);
  const loadingLens = ramda.lensPath([reduxEntity, 'loading']);
  const loading = ramda.view(loadingLens, newProps);
  const style = {
    width: '100%',
    marginBottom: '0px'
  };
  const isMobileAppleDevice = checkBrowser.isMobile('iPod|iPhone|iPad');
  const customHelpText = 'Tap here to use your device to take a picture, select a photo from your library or select an image file from the Apple Files app.';
  const uploadButton = enabled ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Upload, {
    onUpload: handleUpload,
    loading: loading,
    helpText: isMobileAppleDevice ? customHelpText : null
  }), isMobileAppleDevice ? '' : /*#__PURE__*/React.createElement(Snapper, {
    onSave: handleUpload
  })) : null;
  let displayImages;
  if (imageSrc && imageSrc.length) {
    const imageParentContentPath = parentPath(parentPath(path));
    const imageSiblings = getValue(imageParentContentPath, content);
    let images = [];

    // Photos in collections have a remotePath property and _created props
    if (imageSiblings && Object.values(imageSiblings)) {
      images = (Object.values(imageSiblings) || []).sort(pathComparator('_created')).filter(n => n && n.remotePath && (n.remotePath._cache || n.remotePath._downloadURL)).map(n => n.remotePath._downloadURL || n.remotePath._cache);
    } else {
      // Photos by themselves have just a url string
      // to work with the lightbox we need to pop it in an array
      images = [imageSrc];
    }
    displayImages = /*#__PURE__*/React.createElement(Lightbox, {
      selectedIndex: images.indexOf(imageSrc),
      images: images,
      style: style,
      enableZoom: true
    });
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "t-ppf-".concat(testingIdentifier, " tqc-control tqc-photo-picker-field"),
    style: {
      minHeight: computeHeightInPixels(newProps)
    }
  }, label ? /*#__PURE__*/React.createElement(Label, {
    value: label
  }) : undefined, displayImages || uploadButton);
};
PhotoPickerField.propTypes = {
  enabled: PropTypes.bool.isRequired,
  reduxEntity: PropTypes.string,
  path: PropTypes.string.isRequired,
  mdRows: PropTypes.number,
  label: PropTypes.string,
  value: PropTypes.shape({
    _cache: PropTypes.string,
    _downloadURL: PropTypes.string
  }),
  reportActions: PropTypes.shape({
    serverUpload: PropTypes.func,
    uploadClear: PropTypes.func
  }).isRequired,
  testingIdentifier: PropTypes.string,
  content: PropTypes.shape({}).isRequired,
  userID: PropTypes.string.isRequired,
  maxFileSize: PropTypes.shape({}),
  highResolutionImages: PropTypes.bool,
  notificationActions: PropTypes.shape({
    error: PropTypes.func
  }).isRequired,
  onChange: PropTypes.func.isRequired
};
export { PhotoPickerField as UncomposedPhotoPickerField };
export default entityComposition(PhotoPickerField, ['settings'], {
  enabled: enabledSelector,
  label: labelSelector,
  value: valueSelector,
  path: newPathSelector,
  content: getContent,
  maxFileSize: state => {
    var _state$settings;
    return (_state$settings = state.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.settings) === null || _state$settings === void 0 || (_state$settings = _state$settings.configuration) === null || _state$settings === void 0 ? void 0 : _state$settings.maxFileSize;
  },
  highResolutionImages: state => {
    var _state$settings2;
    return (_state$settings2 = state.settings) === null || _state$settings2 === void 0 || (_state$settings2 = _state$settings2.settings) === null || _state$settings2 === void 0 || (_state$settings2 = _state$settings2.configuration) === null || _state$settings2 === void 0 ? void 0 : _state$settings2.highResolutionImages;
  },
  onChange: logValueOnChange
}, ['notification']);